// Building blocks of the networking UI shared by every part (feature spec 17, phase 1; spec 11 6.15): the gate, the
// network admin permission, presentations of network and attachment states, the words of waiting runs (6.15.17), run
// labels (6.15.11) and the error mapping (6.15.18).
import {
  Ban,
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  Hourglass,
  LoaderCircle,
  Network as NetworkIcon,
  Trash2,
  Unplug,
} from 'lucide-react';
import type {
  AttachmentSize,
  AttachmentState,
  NetworkingConfig,
  NetworkStatus,
  Run,
  RunSummary,
  WaitingFor,
} from '../api/types';
import { useAuth, usePermission } from '../auth/context';
import type { Presentation } from '../components/presentation';
import { formatClock, formatRelative } from '../lib/format';
import { strings } from '../strings';

const s = strings.networks;
const rq = strings.networks.requests;
const ep = strings.networks.entryPoints;

export { NetworkIcon };

const OFF: NetworkingConfig = { enabled: false, providers: [] };

/** `config.networking` (off when the API doesn't send it). Everything of spec 11 6.15 exists only while `enabled`. */
export function useNetworking(): NetworkingConfig {
  return useAuth().config.networking ?? OFF;
}

/** Whether the user may manage networks (`manage-networks`, listed only while networking is on). */
export function useManageNetworks(): boolean {
  return usePermission('manage-networks');
}

export function providerLabel(provider: string): string {
  return s.providers[provider] ?? provider;
}

export const SIZE_ORDER: readonly AttachmentSize[] = ['small', 'medium', 'large', 'xlarge'];
export const SIZE_PREFIX: Record<AttachmentSize, number> = { small: 23, medium: 22, large: 21, xlarge: 20 };

/** "Small · /23 · 512 addresses" (spec 11 4.10). */
export function sizeOptionLabel(size: AttachmentSize): string {
  const prefix = SIZE_PREFIX[size];
  return s.sizeOption(s.sizes[size], prefix, 2 ** (32 - prefix));
}

/** The role subnets of an attachment block (layout 1, spec 11 4.10) with each role's capacity (size minus 5). */
export function roleSubnets(cidr: string): { role: string; cidr: string; capacity: number }[] {
  const match = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/.exec(cidr);
  if (!match) return [];
  const base = ((+match[1] * 256 + +match[2]) * 256 + +match[3]) * 256 + +match[4];
  const prefix = +match[5];
  const block = 2 ** (32 - prefix);
  const ip = (n: number) => [24, 16, 8, 0].map((shift) => Math.floor(n / 2 ** shift) % 256).join('.');
  const roles: [string, number, number][] = [
    ['apps', 0, prefix + 2],
    ['functions', block / 4, prefix + 3],
    ['endpoints', (block * 3) / 8, prefix + 3],
    ['data', block / 2, prefix + 3],
  ];
  return roles.map(([role, offset, length]) => ({
    role,
    cidr: `${ip(base + offset)}/${length}`,
    capacity: 2 ** (32 - length) - 5,
  }));
}

/**
 * AWS layout 1 (spec 18 6.4; feature spec 17, phase 4): each role's subnets in zones `a` and `b` ("public · a
 * 10.65.5.0/26 · b 10.65.5.64/26"); `apps` exists only behind an internal load balancer (phase 5) but is reserved.
 */
export function awsRoleSubnets(cidr: string): { role: string; a: string; b: string }[] {
  const match = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/.exec(cidr);
  if (!match) return [];
  const base = ((+match[1] * 256 + +match[2]) * 256 + +match[3]) * 256 + +match[4];
  const prefix = +match[5];
  const block = 2 ** (32 - prefix);
  const ip = (n: number) => [24, 16, 8, 0].map((shift) => Math.floor(n / 2 ** shift) % 256).join('.');
  const roles: [string, number, number, number][] = [
    ['public', block / 4, (block * 5) / 16, prefix + 4],
    ['data', (block * 3) / 8, (block * 7) / 16, prefix + 4],
    ['transit', block / 2, block / 2 + 16, 28],
    ['apps', 0, block / 8, prefix + 3],
  ];
  return roles.map(([role, a, b, length]) => ({
    role,
    a: `${ip(base + a)}/${length}`,
    b: `${ip(base + b)}/${length}`,
  }));
}

/**
 * GCP layout 1 (spec 18 6.4; feature spec 17, phase 6): the `apps` subnet (P+2 at 0) and the `psa` range of private
 * services access (min(P+2, 24) at 1/2), the addresses an attachment's side panel shows.
 */
export function gcpRoleSubnets(cidr: string): { role: string; cidr: string }[] {
  const match = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/.exec(cidr);
  if (!match) return [];
  const base = ((+match[1] * 256 + +match[2]) * 256 + +match[3]) * 256 + +match[4];
  const prefix = +match[5];
  const block = 2 ** (32 - prefix);
  const ip = (n: number) => [24, 16, 8, 0].map((shift) => Math.floor(n / 2 ** shift) % 256).join('.');
  return [
    { role: 'apps', cidr: `${ip(base)}/${prefix + 2}` },
    { role: 'psa', cidr: `${ip(base + block / 2)}/${Math.min(prefix + 2, 24)}` },
  ];
}

/** The capacity of each role in a size (`apps` of `small`: 123). */
export function roleCapacities(size: AttachmentSize): Record<string, number> {
  return Object.fromEntries(roleSubnets(`0.0.0.0/${SIZE_PREFIX[size]}`).map((r) => [r.role, r.capacity]));
}

export const networkStatusPresentation: Record<NetworkStatus, Presentation> = {
  new: { label: s.status.new, tone: 'neutral', icon: CircleDashed },
  active: { label: s.status.active, tone: 'success', icon: CircleCheck },
  failed: { label: s.status.failed, tone: 'danger', icon: CircleX },
  destroyed: { label: s.status.destroyed, tone: 'neutral', icon: Trash2 },
};

const ATTACHMENT_PRESENTATION: Record<AttachmentState, Presentation> = {
  pending: { label: s.attachmentState.pending, tone: 'warning', icon: Clock },
  'spoke-ready': { label: s.attachmentState['spoke-ready'], tone: 'info', icon: Hourglass },
  connecting: { label: s.attachmentState.connecting, tone: 'info', icon: LoaderCircle },
  connected: { label: s.attachmentState.connected, tone: 'success', icon: CircleCheck },
  detaching: { label: s.attachmentState.detaching, tone: 'info', icon: Unplug },
  detached: { label: s.attachmentState.detached, tone: 'neutral', icon: Ban },
  failed: { label: s.attachmentState.failed, tone: 'danger', icon: CircleX },
};

/** An attachment state as a badge (icon, word and tone, never color alone); `waitsForApply` overrides it. */
export function attachmentPresentation(state: AttachmentState, waitsForApply = false): Presentation {
  if (waitsForApply) return { label: s.waitsForApply, tone: 'warning', icon: Hourglass };
  return ATTACHMENT_PRESENTATION[state];
}

/** The neutral presentation of a connect run that waits for permissions (`NETWORK_GRANT_PENDING`, U4.2). */
export const grantPendingPresentation = (label: string): Presentation => ({
  label,
  tone: 'neutral',
  icon: Clock,
});

/** Whether a run failed only because the network's grant wasn't active yet: never shown as a red failure. */
export function isGrantPending(run: Pick<Run, 'status' | 'error'>): boolean {
  return run.status === 'failed' && run.error?.code === 'NETWORK_GRANT_PENDING';
}

/**
 * The words of a waiting run (6.15.17): "Waiting for Production's apply, started 6 min ago by nina", "Retrying at
 * 10:42 (waiting for permissions)", "Waiting: networking is switched off in this installation.", or "Queued".
 */
export function waitingWords(waitingFor: WaitingFor | null | undefined, now = Date.now()): string {
  if (!waitingFor) return s.waiting.queued;
  switch (waitingFor.reason) {
    case 'not-before':
      return waitingFor.until ? s.waiting.retrying(formatClock(waitingFor.until)) : s.waiting.retryingSoon;
    case 'networking-disabled':
      return s.waiting.networkingDisabled;
    default: {
      const target = waitingFor.target?.name ?? s.runLabels.aDeployment;
      const kind = waitingFor.runKind ? s.runKindLower[waitingFor.runKind] : 'run';
      let text = s.waiting.forRun(target, kind);
      if (waitingFor.since) text += s.waiting.started(formatRelative(waitingFor.since, now));
      if (waitingFor.by) text += s.waiting.by(displayActor(waitingFor.by));
      return text;
    }
  }
}

/** `system:network` reads "Nebisys". */
export function displayActor(actor: string): string {
  return actor.startsWith('system') ? s.runLabels.nebisys : actor;
}

/**
 * A network run's label (6.15.11): its kind for a full-scope run ("Plan"), "Connect orders-prod" / "Detach orders-prod"
 * for an attachment run, the deployment named by `attachmentDeploymentName`, else `deploymentName` (the page's own data).
 */
export function networkRunLabel(
  run: Pick<RunSummary, 'kind' | 'scope' | 'attachmentMode'> & { attachmentDeploymentName?: string | null },
  deploymentName?: string | null,
): string {
  if (run.scope !== 'attachment') return strings.status.runKind[run.kind];
  const name = run.attachmentDeploymentName ?? deploymentName ?? s.runLabels.aDeployment;
  switch (run.attachmentMode) {
    case 'detach':
      return s.runLabels.detach(name);
    case 'decision':
      return s.runLabels.decision(name);
    default:
      return s.runLabels.connect(name);
  }
}

/** Whether a run acts on a network (`target.kind: network`, or `networkId` without a deployment). */
export function isNetworkRun(run: Pick<Run, 'target' | 'networkId' | 'deploymentId'>): boolean {
  return run.target?.kind === 'network' || (!!run.networkId && !run.deploymentId);
}

/** What the UI offers next to a networking error's sentence (6.15.18). */
export type NetworkErrorAction =
  | 'plan-network'
  | 'detach'
  | 'open-network-settings'
  | 'open-deployment-settings'
  | 'add-supernet'
  | 'open-detach-run'
  | 'apply'
  | 'show-attachments'
  | 'retry'
  | 'open-topology'
  | 'open-traffic'
  | null;

export interface NetworkErrorInfo {
  sentence: string;
  action: NetworkErrorAction;
  /** The action is for network admins only; others get no action (or `askSentence`). */
  adminOnly: boolean;
  /** Retry can't help: the error needs a network apply. */
  needsNetworkApply: boolean;
}

export interface NetworkErrorContext {
  network?: string | null;
  deployment?: string | null;
  region?: string | null;
  account?: string | null;
  /** The server's message (Problem `detail` or the error's `message`). */
  message?: string | null;
  /** Feature spec 17, phase 3: the entry route an origin error is about. */
  route?: string | null;
}

/** The sentence and role-aware action of a networking error code (6.15.18); null for codes it doesn't know. */
export function networkErrorInfo(code: string, context: NetworkErrorContext = {}): NetworkErrorInfo | null {
  const network = context.network ?? s.nav.kind;
  const deployment = context.deployment ?? s.runLabels.aDeployment;
  const info = (
    sentence: string,
    action: NetworkErrorAction = null,
    adminOnly = false,
    needsNetworkApply = false,
  ): NetworkErrorInfo => ({ sentence, action, adminOnly, needsNetworkApply });
  const e = s.errors;
  switch (code) {
    case 'NETWORK_ATTACHMENT_UNEXPECTED_CHANGE':
    case 'NETWORK_APPLY_NEEDED':
      return info(e.applyNeeded(network), 'plan-network', true, true);
    case 'NETWORK_DESTROYED':
      return info(e.destroyed(network, deployment), 'detach');
    case 'ATTACH_NOT_PERMITTED':
      return info(e.attachNotPermitted(network));
    case 'NETWORK_REGION_NOT_ENABLED':
      return info(e.regionNotEnabled(network, context.region ?? ''), 'open-network-settings', true);
    case 'NETWORK_RUN_PENDING':
      return info(e.runPending(network));
    case 'IPAM_SUBNET_TOO_SMALL':
      return info(context.message ?? code, 'open-deployment-settings');
    case 'IPAM_EXHAUSTED':
      return info(context.message ?? code, 'add-supernet', true);
    case 'NETWORK_STEPS_UNAVAILABLE':
      return info(e.stepsUnavailable);
    case 'NETWORK_NOT_APPLIED':
      return info(e.notApplied(network), 'plan-network', true);
    case 'NETWORK_ACCOUNT_NOT_ALLOWED':
      return info(e.accountNotAllowed(context.account ?? deployment), 'open-network-settings', true);
    case 'NETWORK_DETACH_FAILED':
      return info(e.detachFailed(network), 'open-detach-run');
    case 'NETWORK_DETACH_UNEXPECTED_CHANGE':
      return info(e.detachUnexpectedChange(deployment), 'apply');
    case 'NETWORK_GRANT_PENDING':
      return info(s.waiting.grantPendingCallout(deployment));
    case 'NETWORK_CHANGE_NEEDS_DETACH':
      return info(e.changeNeedsDetach(network, deployment));
    case 'NETWORK_RETAINED_ELSEWHERE':
      return info(e.retainedElsewhere(deployment, network));
    case 'NETWORKING_DISABLED':
      return info(s.switchedOff);
    case 'NETWORK_HAS_ATTACHMENTS':
      return info(e.hasAttachments, 'show-attachments');
    case 'NETWORK_NOT_REMOVABLE':
      return info(e.notRemovable, 'show-attachments');
    case 'NETWORK_HAS_RETAINED_ADDRESSES':
      return info(e.hasRetained(network), 'show-attachments');
    case 'NETWORK_REGION_IN_USE':
    case 'NETWORK_ACCOUNT_IN_USE':
    case 'NETWORK_ACCOUNT_OTHER_TENANT':
    case 'TOPOLOGY_NOT_AVAILABLE':
    case 'FIREWALL_NOT_AVAILABLE':
    case 'ENTRY_KIND_NOT_AVAILABLE':
      return info(context.message ?? code);
    case 'ATTACHMENT_STATE_CHANGED':
      return info(e.stateChanged);
    // Feature spec 17, phase 2 (6.15.27).
    case 'MESH_TOO_LARGE':
      return info(context.message ?? rq.codes.MESH_TOO_LARGE, 'open-topology', true);
    case 'HUB_TRANSIT_REQUIRES_FIREWALL':
      return info(rq.codes.HUB_TRANSIT_REQUIRES_FIREWALL, 'open-topology', true);
    case 'TOPOLOGY_CHOICE_REQUIRED':
      return info(rq.codes.TOPOLOGY_CHOICE_REQUIRED, 'open-topology', true);
    case 'NETWORK_REQUEST_PENDING':
      return info(rq.blockedUntil(network), 'open-traffic', true);
    case 'REQUEST_DECISION_INVALID':
      return info(rq.requestChanged);
    case 'DECISION_BY_REQUESTER':
      return info(rq.sameApprover);
    case 'FIREWALL_RULE_GROUPS_LIMIT':
      return info(context.message ?? code, 'open-traffic', true);
    case 'NETWORK_DECISION_MISMATCH':
      return info(rq.decisionMismatch(network), 'plan-network', true, true);
    case 'NETWORK_REQUEST_TARGET_MISMATCH':
      return info(context.message ?? code, 'open-deployment-settings');
    case 'SEGMENTATION_TOO_MANY_RULES':
      return info(rq.tooManyRules(network, deployment), 'open-traffic', true);
    // Feature spec 17, phase 4 (6.15.32).
    case 'AWS_NETWORK_REGION_UNKNOWN':
      return info(context.message ?? code, 'open-network-settings', true);
    case 'AWS_CENTRAL_EGRESS_HUB_TOO_SMALL':
      return info(strings.networks.topology.awsHubTooSmall, 'open-network-settings', true);
    case 'AWS_EGRESS_PUBLIC_SUBNETS':
      return info(context.message ?? strings.networks.topology.publicSubnetsEgress);
    case 'AWS_ATTACHED_DATABASE_PRIVATE':
      return info(e.awsDatabasePrivate(deployment));
    case 'AWS_SHARE_NEEDS_ACCEPTANCE':
      return info(strings.networks.topology.awsShareNeedsAcceptance);
    case 'AWS_ORGANIZATION_NOT_CHECKED':
      return info(context.message ?? code, 'open-network-settings', true);
    // Feature spec 17, phase 6 (6.15.36).
    case 'GCP_NETWORK_REGION_UNKNOWN':
      return info(context.message ?? code, 'open-network-settings', true);
    case 'GCP_FIREWALL_TIER_NOT_AVAILABLE':
    case 'GCP_FIREWALL_NEEDS_EGRESS':
    case 'GCP_EGRESS_WILDCARD_NOT_SUPPORTED':
      return info(strings.networks.gcp.codes[code], 'open-network-settings', true);
    case 'GCP_DEPLOYING_IDENTITY_UNKNOWN':
      return info(context.message ?? strings.networks.gcp.codes[code], 'open-network-settings', true);
    case 'GCP_HUB_TRANSIT_NOT_SUPPORTED':
      return info(strings.networks.gcp.viaHubNotSupported, 'open-topology', true);
    case 'GCP_SQL_NOT_REACHABLE':
      return info(strings.networks.gcp.sqlNotReachable);
    // Feature spec 17, phase 8 (6.15 item 39): the server's sentence where it names the zone, host or app.
    case 'DNS_ZONE_REQUIRED':
      return info(context.message ?? ep.codes.DNS_ZONE_REQUIRED(''));
    case 'CLOUDFLARE_ZONE_IN_USE':
    case 'CLOUDFLARE_ROUTE_SHAPE':
    case 'CLOUDFLARE_CERTIFICATE_NOT_COVERED':
    case 'CLOUDFLARE_MANAGED_RULES_FREE':
      return info(context.message ?? code);
    case 'CLOUDFLARE_WAF_DETECTION_NEEDS_ENTERPRISE':
      return info(ep.codes.CLOUDFLARE_WAF_DETECTION_NEEDS_ENTERPRISE);
    case 'CLOUDFLARE_PHASE_IN_USE':
    case 'CLOUDFLARE_ORIGIN_DOMAIN_NEEDED':
    case 'CLOUDFLARE_ZONE_MISMATCH':
    case 'CLOUDFLARE_PLAN_MISMATCH':
    case 'CLOUDFLARE_RECORD_IN_USE':
    case 'CLOUDFLARE_ZONE_UNAVAILABLE':
      return info(context.message ?? code, null, true);
    case 'CLOUDFLARE_CHECK_FAILED':
      return info(context.message ?? code, 'retry');
    case 'AWS_SHARED_ALB_LOCKED':
    case 'AWS_ORIGIN_EXPOSURE_CONFLICT':
      return info(context.message ?? code);
    // Feature spec 17, phase 7 (6.15.38).
    case 'ORIGIN_DETACH_BLOCKED':
      return info(context.message ?? code);
    case 'GCP_ARMOR_ENTERPRISE_SUBSCRIPTION':
      return info(ep.codes.GCP_ARMOR_ENTERPRISE_SUBSCRIPTION(network));
    case 'ADD_ON_NOT_AVAILABLE':
      return info(context.message ?? code);
    // Feature spec 17, phase 5 (6.15.34).
    case 'COMMITMENT_NOT_CONFIRMED':
      return info(context.message ?? code);
    case 'ROUTE_SHAPE_NOT_SUPPORTED':
      return info(ep.codes.ROUTE_SHAPE_NOT_SUPPORTED);
    case 'DNS_ZONE_NOT_SUPPORTED':
      return info(ep.codes.DNS_ZONE_NOT_SUPPORTED);
    case 'AWS_VPC_ORIGIN_NEEDS_PRIVATE_SUBNETS':
      return info(context.message ?? code, 'open-network-settings', true);
    case 'AWS_EXPRESS_EXPOSURE_CHANGE':
      return info(context.message ?? ep.codes.AWS_EXPRESS_EXPOSURE_CHANGE(context.route ?? 'The app'));
    case 'AWS_INTERNAL_ALB_UNREACHABLE':
      return info(context.message ?? ep.codes.AWS_INTERNAL_ALB_UNREACHABLE(context.route ?? 'The app'));
    case 'AWS_IMAGE_HOST_EGRESS':
      return info(context.message ?? code, 'open-network-settings', true);
    case 'AWS_SHIELD_ALREADY_SUBSCRIBED':
      return info(ep.codes.AWS_SHIELD_ALREADY_SUBSCRIBED);
    case 'AWS_SHIELD_NOT_CHECKED':
      return info(ep.codes.AWS_SHIELD_NOT_CHECKED, 'retry');
    // Feature spec 17, phase 3 (6.15.29).
    case 'ORIGIN_SECRET_UNAVAILABLE':
      return info(ep.codes.ORIGIN_SECRET_UNAVAILABLE(network), 'plan-network', true, true);
    case 'ORIGIN_SECRET_REGENERATED':
      return info(ep.codes.ORIGIN_SECRET_REGENERATED(context.route ?? '', deployment), 'apply');
    case 'ROUTE_ORIGIN_NOT_ATTACHED':
      return info(ep.codes.ROUTE_ORIGIN_NOT_ATTACHED(deployment, network), 'open-deployment-settings');
    case 'ROUTE_ORIGIN_UNKNOWN':
      return info(ep.codes.ROUTE_ORIGIN_UNKNOWN);
    case 'AZURE_APP_GATEWAY_HUB_TOO_SMALL':
      return info(ep.codes.AZURE_APP_GATEWAY_HUB_TOO_SMALL, 'open-network-settings', true);
    case 'CERTIFICATE_ACCESS_PENDING':
      return info(context.message ?? ep.codes.CERTIFICATE_ACCESS_PENDING(''), 'plan-network', true);
    case 'ORIGIN_ROTATION_PENDING':
      return info(ep.codes.ORIGIN_ROTATION_PENDING);
    case 'ORIGIN_SECRET_NOT_USED':
      return info(ep.codes.ORIGIN_SECRET_NOT_USED);
    case 'ORIGIN_UNEXPECTED_PRIVATE_ENDPOINT':
      return info(ep.codes.ORIGIN_UNEXPECTED_PRIVATE_ENDPOINT);
    case 'ORIGIN_ACCESS_NEEDS_CHOICE':
    case 'ORIGIN_ACCESS_NOT_AVAILABLE':
    case 'ORIGIN_PLATFORM_NOT_SUPPORTED':
    case 'ORIGIN_PLATFORM_PUSH_NEEDS_CHOICE':
    case 'ENTRY_DOMAIN_REQUIRED':
    case 'CERTIFICATE_KIND_NOT_AVAILABLE':
    case 'CONNECTION_THROUGH_EDGE':
      return info(context.message ?? code);
    default:
      return null;
  }
}

/**
 * Why a request target placed on Google Cloud can't be reached (feature spec 17, phase 6; spec 11 6.15.36): Cloud SQL
 * through private services access (`GCP_SQL_NOT_REACHABLE`) and Cloud Run (`NETWORK_REQUEST_TARGET_TYPE`); null when the
 * target isn't on Google Cloud or isn't a request target type at all.
 */
export function gcpTargetReason(
  resource: { type: string; placement?: { provider?: string | null } | null },
  privatelyReachable: boolean,
): string | null {
  if (!privatelyReachable || resource.placement?.provider !== 'gcp') return null;
  return resource.type === 'data.sql'
    ? strings.networks.gcp.sqlNotReachable
    : strings.networks.gcp.runNeedsIlb;
}
